// Pure detection logic for the integration-disconnect watcher
// (hooks/use-integration-health.ts). Kept free of React and network calls so the
// "what counts as disconnected" rules are unit-testable on their own.
//
// The rules deliberately mirror what the Integrations page renders as
// "Reconnect required" — the notification must never disagree with the page it
// links to. "Never connected" is NOT disconnected: only an integration the user
// (or their org) set up that has since stopped working raises a notification.

import type { EmailIntegrationStatus, ZohoIntegration } from "@/api/endpoints/integrations";
import type { D365BusinessCentralIntegration } from "@/api/endpoints/d365BusinessCentral";
import type { ComposioStatus } from "@/api/endpoints/composio";

/** Notification type shared with email-backend's mailbox-disconnect emitter. */
export const INTEGRATION_DISCONNECTED_TYPE = "integration_disconnected";

/** One integration's verdict. `unknown` = the status fetch failed; never acted on. */
export type IntegrationVerdict =
  | { key: string; state: "broken"; name: string; body: string }
  | { key: string; state: "ok" }
  | { key: string; state: "unknown" };

/** Connection statuses the Zoho / D365 backends write when a token stops refreshing. */
const BROKEN_ZOHO_STATUSES = new Set(["pending_reauth", "error"]);

function broken(key: string, name: string, body: string): IntegrationVerdict {
  return { key, state: "broken", name, body };
}

/**
 * Email only ever CLEARS from here — it never raises. `healthy` on the status
 * endpoint is still a staleness heuristic ("no mail in 48h"), so a quiet mailbox
 * over a weekend would read as disconnected. The real signal (Aurinko 401) is
 * raised server-side by email-backend, on both user requests and the 30-min
 * sync sweep. A mailbox that is receiving mail, though, is certainly working.
 */
export function emailVerdict(status: EmailIntegrationStatus | null): IntegrationVerdict {
  const key = "email";
  if (!status) return { key, state: "unknown" };
  const providers = [status.google, status.microsoft].filter((p) => p?.connected);
  if (providers.length && providers.every((p) => p.healthy)) return { key, state: "ok" };
  return { key, state: "unknown" };
}

export function zohoVerdicts(list: ZohoIntegration[] | null): IntegrationVerdict[] {
  if (!list) return [{ key: "zoho_books", state: "unknown" }, { key: "zoho_analytics", state: "unknown" }];
  const isAnalytics = (i: ZohoIntegration) => i.integration_type === "analytics";
  const isBroken = (i: ZohoIntegration) => BROKEN_ZOHO_STATUSES.has(i.connection_status);
  const books = list.filter((i) => !isAnalytics(i));
  const analytics = list.filter(isAnalytics);
  return [
    books.some(isBroken)
      ? broken("zoho_books", "Zoho Books", "Zoho stopped accepting our connection. Reconnect it to resume ERP sync.")
      : { key: "zoho_books", state: "ok" },
    analytics.some(isBroken)
      ? broken("zoho_analytics", "Zoho Analytics", "Zoho Analytics stopped accepting our connection. Reconnect it to resume reports sync.")
      : { key: "zoho_analytics", state: "ok" },
  ];
}

export function d365BcVerdict(integration: D365BusinessCentralIntegration | null): IntegrationVerdict {
  const key = "d365_bc";
  if (!integration) return { key, state: "unknown" };
  // Same rule as IntegrationsPage `d365BcNeedsAttention`: the row stays
  // `connected` after the token refresh fails; only connection_status flips.
  if (integration.connected && integration.connection_status && integration.connection_status !== "connected") {
    return broken(key, "Dynamics 365 Business Central", "Microsoft stopped accepting our connection. Reconnect it to resume the nightly sync.");
  }
  return { key, state: "ok" };
}

export function composioVerdicts(status: ComposioStatus | null, displayName: (slug: string) => string): IntegrationVerdict[] {
  if (!status) return [];
  const expired = new Set((status.needs_reconnect ?? []).map((t) => t.toLowerCase()));
  const out: IntegrationVerdict[] = [];
  for (const slug of expired) {
    const name = displayName(slug);
    out.push(broken(`composio:${slug}`, name, `Ask Ubik can no longer use ${name}. Reconnect it in Integrations.`));
  }
  // Everything connected right now is healthy — clears an earlier expiry notification.
  for (const slug of status.connected ?? []) {
    const s = slug.toLowerCase();
    if (!expired.has(s)) out.push({ key: `composio:${s}`, state: "ok" });
  }
  return out;
}

/**
 * Dedup key for one disconnect notification. Includes the UTC day so a still-
 * broken integration re-reminds at most once a day after the user has read it
 * (the backend dedups only against UNREAD rows). The `email` key matches
 * email-backend's mailbox emitter so the two sources never double-notify.
 */
export function disconnectDedupKey(key: string, now: Date = new Date()): string {
  return `${INTEGRATION_DISCONNECTED_TYPE}:${key}:${now.toISOString().slice(0, 10)}`;
}
